import {Component, computed, DestroyRef, inject, OnDestroy, OnInit} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {EmptyStateComponent} from '@/app/shared/components/empty-state/empty-state.component';
import {ActivatedRoute, Router} from '@angular/router';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TitlePagesComponent} from '@/app/shared/components/title-pages-component/title-pages-component';
import {
  ResultsTableComponent
} from '@/app/features/Resultados/components/results-table-component/results-table.component';
import {
  CampanaSelectComponent,
  Option
} from '@/app/features/campaña/components/campana-select/campana-select-component';
import {CampaignStoreService} from '@/app/features/campaña/services/campaign-store.service';
import {ResultStateService} from '@/app/features/Resultados/services/result.state.service';
import {UserRole, UserRoleStore} from '@/app/core/states/user-role-stare/client-role-state.store';
import {ClienteSelectComponent} from '@/app/features/campaña/components/cliente-select/cliente-select-component';
import {ClientsService} from '@/app/features/clientes/services/clients.service';
import {CampaignService} from '@/app/features/campaña/services/campaign.service';
import {ClientStore} from '@/app/core/states/clientes-state/client-state.store';
import {SurveyResponsesService} from '@/app/features/Resultados/services/survey-responses.service';
import {
  AnswerResponseDto,
  SurveySubmissionSummaryDto,
  WorkerDemographicsDto,
} from '@/app/features/Resultados/interface/survey-responses.interface';
import {Resultado} from '@/app/features/Resultados/interface/result.inteface';
import {ThemeService} from '@/app/core/services/theme/theme.service';

@Component({
  selector: 'app-resultados-component',
  standalone: true,
  templateUrl: './resultados-component.html',
  imports: [
    FormsModule,
    ResultsTableComponent,
    CampanaSelectComponent,
    TitlePagesComponent,
    ClienteSelectComponent,
    EmptyStateComponent
  ],
  styleUrl: './resultados-component.scss',
})
export class ResultadosComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  readonly destroyRef = inject(DestroyRef);
  private clientStore = inject(ClientStore);
  private userState = inject(UserRoleStore);
  private themeService = inject(ThemeService);
  private store = inject(CampaignStoreService);
  private clientsService = inject(ClientsService);
  private campaignService = inject(CampaignService);
  private resultStateService = inject(ResultStateService);
  private surveyResponsesService = inject(SurveyResponsesService);

  protected themeContent = this.themeService.themeContent;
  protected resultsContent = computed(() => this.themeContent()?.resultsComponent);
  protected tablePreviousIcon = computed(() => this.themeService.getTheme()?.icons?.tablePreviousIcon);
  protected tableNextIcon = computed(() => this.themeService.getTheme()?.icons?.tableNextIcon);

  pageData = computed(() => ({
    title: this.resultsContent()?.title || 'Resultados',
    subTitle: this.resultsContent()?.description || 'Subtitulo'
  }));

  campaignOptions: Option[] = [];
  selectedCampaignOption?: Option;
  loadingCampaignOptions = false;

  get resultados(): Resultado[] {
    return this._filteredResults;
  }

  private groupByToken(submissions: SurveySubmissionSummaryDto[]): Resultado[] {
    const agrupados = new Map<string, string[]>();

    submissions.forEach(submission => {
      if (!agrupados.has(submission.token)) {
        agrupados.set(submission.token, []);
      }

      agrupados.get(submission.token)!.push(submission.guideId);
    });

    return Array.from(agrupados.entries()).map(([token, cuestionario]) => {
      const sortedCuestionario = cuestionario.sort((a, b) => {
        const order = { 'guia-i': 1, 'guia-ii': 2, 'guia-iii': 3 };
        return order[a as keyof typeof order] - order[b as keyof typeof order];
      });
      return {
        token,
        cuestionario: sortedCuestionario
      };
    });
  }

  isAdmin: boolean = false;

  // Datos de los endpoints
  surveySubmissions: SurveySubmissionSummaryDto[] = [];
  surveyAnswers: AnswerResponseDto[] = [];
  workerDemographics: WorkerDemographicsDto[] = [];

  // Estados de carga
  loadingSubmissions = false;
  loadingAnswers = false;
  loadingDemographics = false;

  // Flag to track if we're restoring from saved state
  private isRestoringFromSavedState = false;

  // Paginación
  currentPage = 0;
  pageSize = 10;
  totalElements = 0;
  pageSizeOptions = [10, 20, 50, 100];

  // Filtered results for pagination
  private _filteredResults: Resultado[] = [];

  // Propiedades calculadas para la paginación
  get totalPages(): number {
    return Math.ceil(this.totalElements / this.pageSize) || 1;
  }

  get paginationLabel(): string {
    const start = this.currentPage * this.pageSize + 1;
    const end = Math.min((this.currentPage + 1) * this.pageSize, this.totalElements);
    return `${start}–${end} de ${this.totalElements}`;
  }

  ngOnInit(): void {
    // Check if we have saved selections (user is returning from detailed results)
    const savedClientSelection = this.resultStateService.getClientSelection();
    const savedCampaignSelection = this.resultStateService.getCampaignSelection();
    const savedPage = this.resultStateService.getCurrentPage();

    console.log('ngOnInit - Saved selections:', { savedClientSelection, savedCampaignSelection, savedPage });

    if (savedClientSelection && savedCampaignSelection) {
      // Restore saved selections - user is returning from detailed results
      this.isRestoringFromSavedState = true;
      this.selectedClientOption = { value: savedClientSelection.clientId, label: savedClientSelection.clientName };
      this.selectedCampaignOption = { value: savedCampaignSelection.campaignId, label: savedCampaignSelection.campaignName };

      // Restore pagination FIRST before loading any data
      if (savedPage > 0) {
        this.currentPage = savedPage;
        console.log('Restored pagination to page:', this.currentPage);
      }

      // Restore pageSize
      const savedPageSize = this.resultStateService.getPageSize();
      if (savedPageSize > 0) {
        this.pageSize = savedPageSize;
        console.log('Restored pageSize to:', this.pageSize);
      }

      // Load campaign options for the restored client
      this.loadCampaignOptionsForClient(savedClientSelection.clientId);

      // Load survey data with restored selections (now with correct page)
      this.loadSurveySubmissions();

      // Reset the flag after a short delay to allow the subscription to process
      setTimeout(() => {
        this.isRestoringFromSavedState = false;
      }, 100);
    } else {
      // Clear any existing state to ensure fresh start
      this.clearAllState();
      console.log('No saved selections found, starting fresh');
    }

    const user: UserRole | null = this.userState.user();
    if (user === null) {
      return;
    }

    if (user.group === 'USER') {
      // Para user, cargar campañas del cliente logueado usando datos del usuario
      this.isAdmin = false;
      const clientPublicId = user.attributes?.['publicId'] || user.id;
      const clientName =
        user.attributes?.['clientName'] || user.username || 'Usuario';

      this.clientOptions = [{ value: clientPublicId, label: clientName }];
      this.selectedClientOption = this.clientOptions[0];

      this.store.loadCampaigns(clientPublicId);
      // Se limpia cuando recarga la pagina
      this.store.setSelectedCampaign(null);

      this.store.campaignsOptions$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((opts) => {
          this.campaignOptions = [...opts].sort((a, b) => a.label.localeCompare(b.label));
        });

      this.store.selectedCampaign$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((opt) => {
          if (opt) {
            this.selectedCampaignOption = opt;
            this.loadSurveySubmissions();
          }
        });
    } else {
      // Para admin, cargar todos los clientes
      this.isAdmin = true;
      this.clientsService
        .getAllClients()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((clients) => {
          this.clients = clients;
          this.clientOptions = clients.map((client) => ({
            value: client.publicId,
            label: client.name,
          })).sort((a, b) => a.label.localeCompare(b.label));

          this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
            const clienteId = params['clienteId'];
            if (clienteId) {
              const opt = this.clientOptions.find(c => c.value === clienteId);
              if (opt) {
                this.onClientSelect(opt);
              }
            }
          });
        });
    }

    this.resultStateService.clearCuestionario();
    // Clear selections when doing a full reset
    this.resultStateService.clearSelections();
  }

  ngOnDestroy(): void {
    if (this.isAdmin) {
      this.clientStore.resetData();
      this.store.clearCampaigns();
      this.selectedClientOption = undefined;
      this.selectedCampaignOption = undefined;
      this.clientOptions = [];
      this.campaignOptions = [];
    }
  }

  private clearAllState() {
    // Clear all component state to ensure fresh start
    this.surveySubmissions = [];
    this.surveyAnswers = [];
    this.workerDemographics = [];
    this.totalElements = 0;
    this.loadingSubmissions = false;
    this.loadingAnswers = false;
    this.loadingDemographics = false;
    this.loadingCampaignOptions = false;
    this.campaignOptions = [];
    this.selectedCampaignOption = undefined;
    this.selectedClientOption = undefined;
    this._filteredResults = [];
    this.resultStateService.clearSurveyAnswers();
    this.resultStateService.clearAllSurveyAnswersForGuide();
    // Don't clear currentPage here to preserve pagination state
    // this.resultStateService.clearCurrentPage();
    this.resultStateService.clearCuestionario();
    // Clear selections when doing a full reset
    this.resultStateService.clearSelections();
  }

  clientOptions: Option[] = [];
  selectedClientOption?: Option;
  clients: any[] = [];

  // Track previous selections to determine if pagination should reset
  private previousClientOption?: Option;
  private previousCampaignOption?: Option;

  onClientSelect(opt: Option) {
    // Check if client selection actually changed
    const clientChanged = this.previousClientOption?.value !== opt.value;

    // Update previous selection tracking
    this.previousClientOption = opt;
    this.selectedClientOption = opt;

    const currentCampaign = this.selectedCampaignOption;
    this.selectedCampaignOption = undefined;
    this.campaignOptions = [];
    this.loadingCampaignOptions = true;

    // Only reset pagination if client actually changed
    if (clientChanged) {
      this.clearSurveyData();
    } else {
      this.clearSurveyDataWithoutPagination();
    }

    const clientObj = this.clients.find(c => c.publicId === opt.value);
    if (clientObj) {
      this.clientStore.setClient(clientObj);
    }

    this.campaignService
      .getCampaignsByClient(opt.value)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (campaigns) => {
          this.campaignOptions = campaigns.map((campaign) => ({
            value: campaign.publicId,
            label: campaign.name,
          })).sort((a, b) => a.label.localeCompare(b.label));

          // NO auto-seleccionar ninguna campaña - dejar que el usuario elija
          // Solo verificar si la campaña previamente seleccionada pertenece a este cliente
          if (currentCampaign && this.campaignOptions.some(c => c.value === currentCampaign.value)) {
            this.selectedCampaignOption = currentCampaign;
          }
          // NO cargar datos automáticamente - esperar que el usuario seleccione una campaña

          this.loadingCampaignOptions = false;
        },
        error: (error) => {
          console.error('Error loading campaigns for client:', error);
          this.loadingCampaignOptions = false;
        }
      });
  }

  private loadCampaignOptionsForClient(clientId: string) {
    // Load campaigns for the specified client (used when restoring selections)
    this.loadingCampaignOptions = true;
    this.campaignService
      .getCampaignsByClient(clientId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (campaigns) => {
          this.campaignOptions = campaigns.map((campaign) => ({
            value: campaign.publicId,
            label: campaign.name,
          })).sort((a, b) => a.label.localeCompare(b.label));

          // Debug logging
          console.log('Campaign options loaded for client:', clientId, this.campaignOptions);
          console.log('Current selected campaign option:', this.selectedCampaignOption);

          // Ensure the selected campaign option is properly set after options are loaded
          if (this.selectedCampaignOption) {
            const matchingOption = this.campaignOptions.find(opt => opt.value === this.selectedCampaignOption?.value);
            if (matchingOption) {
              this.selectedCampaignOption = matchingOption;
            }
          }

          this.loadingCampaignOptions = false;
        },
        error: (error) => {
          console.error('Error loading campaign options:', error);
          this.loadingCampaignOptions = false;
        }
      });
  }

  private clearSurveyData() {
    this.surveySubmissions = [];
    this.surveyAnswers = [];
    this.workerDemographics = [];
    this.totalElements = 0;
    this.loadingSubmissions = false;
    this.loadingAnswers = false;
    this.loadingDemographics = false;
    this._filteredResults = [];
    // Reset currentPage to 0 when campaign changes
    this.currentPage = 0;
    this.resultStateService.clearSurveyAnswers();
    this.resultStateService.clearAllSurveyAnswersForGuide();
    // Don't clear currentPage here - preserve pagination state
    // this.resultStateService.clearCurrentPage();
    // Clear any cached questionnaire data
    this.resultStateService.clearCuestionario();
    this.resultStateService.clearCachedData(); // Clear cached data
  }

  private clearSurveyDataWithoutPagination() {
    this.surveySubmissions = [];
    this.surveyAnswers = [];
    this.workerDemographics = [];
    this.totalElements = 0;
    this.loadingSubmissions = false;
    this.loadingAnswers = false;
    this.loadingDemographics = false;
    this._filteredResults = [];
    // Don't reset currentPage - preserve pagination state
    this.resultStateService.clearSurveyAnswers();
    this.resultStateService.clearAllSurveyAnswersForGuide();
    // Don't clear currentPage here to preserve pagination state
    // this.resultStateService.clearCurrentPage();
    // Clear any cached questionnaire data
    this.resultStateService.clearCuestionario();
    this.resultStateService.clearCachedData(); // Clear cached data
  }

  onCampaignSelect(opt: Option) {
    // Check if campaign selection actually changed
    const campaignChanged = this.previousCampaignOption?.value !== opt.value;

    // Update previous selection tracking
    this.previousCampaignOption = opt;

    if (this.isAdmin) {
      this.selectedCampaignOption = opt;

      // Only reset pagination if campaign actually changed
      if (campaignChanged) {
          this.clearSurveyData();
      } else {
        this.clearSurveyDataWithoutPagination();
      }

      this.loadSurveySubmissions();
    } else {
      this.store.setSelectedCampaign(opt);

      // Only reset pagination if campaign actually changed
      if (campaignChanged) {
        this.clearSurveyData();
      } else {
        this.clearSurveyDataWithoutPagination();
      }

      // For user, loadSurveySubmissions is called by the subscription to selectedCampaign$
      // No need to call it here to avoid double API calls
    }
  }

  viewResults = async ({ item, guideId }: { item: Resultado, guideId: string }) => {
    // Guardar la página actual antes de navegar
    console.log('viewResults - Saving current page:', this.currentPage);
    this.resultStateService.setCurrentPage(this.currentPage);

    // Save current pageSize
    console.log('viewResults - Saving pageSize:', this.pageSize);
    this.resultStateService.setPageSize(this.pageSize);

    // Save current client and campaign selections for all users
    if (this.selectedClientOption && this.selectedCampaignOption) {
      console.log('viewResults - Saving selections:', {
        client: this.selectedClientOption,
        campaign: this.selectedCampaignOption
      });
      this.resultStateService.setClientSelection(
        this.selectedClientOption.value,
        this.selectedClientOption.label
      );
      this.resultStateService.setCampaignSelection(
        this.selectedCampaignOption.value,
        this.selectedCampaignOption.label
      );
    }

    // Find the submission by token and guideId
    const submission = this.surveySubmissions.find(sub => sub.token === item.token && sub.guideId === guideId);

    if (submission) {
      const resultadoCompleto = {
        ...item,
        guideId: submission.guideId,
        token: submission.token,
        createdAt: submission.createdAt
      };
      this.resultStateService.setCuestionario(resultadoCompleto);

      if (submission.guideId && submission.token) {
        await this.loadSurveyAnswersAsync(submission.guideId, submission.token);
      }

      const routeMap: { [key: string]: string } = {
        'guia-i': 'resultados/guia1',
        'guia-ii': 'resultados/guia2',
        'guia-iii': 'resultados/guia3'
      };

      const targetRoute = routeMap[submission.guideId];

      this.router.navigate([targetRoute]).then(() => {
        window.scrollTo(0, 0);
      });
    } else {
      // Fallback if submission not found, use provided guideId
      const resultadoCompleto = {
        ...item,
        guideId,
        token: item.token
      };
      this.resultStateService.setCuestionario(resultadoCompleto);

      const routeMap: { [key: string]: string } = {
        'guia-i': 'resultados/guia1',
        'guia-ii': 'resultados/guia2',
        'guia-iii': 'resultados/guia3'
      };

      const targetRoute = routeMap[guideId];

      this.router.navigate([targetRoute]).then(() => {
        window.scrollTo(0, 0);
      });
    }
  };

  prevPage(): void {
    if (this.currentPage > 0) {
      this.currentPage--;
      this.loadSurveySubmissions();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.currentPage++;
      this.loadSurveySubmissions();
    }
  }

  goToPage(page: number): void {
    if (page >= 0 && page < this.totalPages) {
      this.currentPage = page;
      this.loadSurveySubmissions();
    }
  }

  onPageSizeChange(): void {
    this.currentPage = 0;
    this.loadSurveySubmissions();
  }

  loadSurveySubmissions() {
    if (!this.selectedCampaignOption) return;

    this.loadingSubmissions = true;
    // Clear existing data immediately to show loading state
    this.surveySubmissions = [];
    this._filteredResults = [];
    this.totalElements = 0;

    const params = {
      campaign: this.selectedCampaignOption.value,
      page: this.currentPage,
      size: this.pageSize,
      sort: ['createdAt,desc']
    };

    this.surveyResponsesService.getSurveySubmissionSummaries(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.surveySubmissions = response.content;
          this.totalElements = response.totalElements;
          this._filteredResults = this.groupByToken(response.content);
          this.loadingSubmissions = false;
        },
        error: (error) => {
          console.error('Error loading survey submissions:', error);
          this.loadingSubmissions = false;
          // Reset data on error
          this.surveySubmissions = [];
          this._filteredResults = [];
          this.totalElements = 0;
        }
      });
  }

  loadSurveyAnswersAsync(guideId: string, token: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.selectedCampaignOption) {
        reject(new Error('No campaign selected'));
        return;
      }

      this.loadingAnswers = true;
      const params = {
        guideId,
        campaign: this.selectedCampaignOption.value,
        token,
        page: 0,
        size: 10000 // Load all answers for this guide and token
      };

      this.surveyResponsesService.getSurveyAnswers(params)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            this.surveyAnswers = response.content;
            this.loadingAnswers = false;
            this.resultStateService.setSurveyAnswersForGuide(this.surveyAnswers, token, guideId);
            resolve();
          },
          error: (error) => {
            console.error('Error loading survey answers:', error);
            this.loadingAnswers = false;
            reject(error);
          }
        });
    });
  }

  loadWorkerDemographics(guideId: string, token: string) {
    if (!this.selectedCampaignOption) return;

    this.loadingDemographics = true;
    const params = {
      guideId,
      campaign: this.selectedCampaignOption.value,
      token,
      page: this.currentPage,
      size: this.pageSize
    };

    this.surveyResponsesService.getWorkerDemographics(params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.workerDemographics = response.content;
          this.loadingDemographics = false;
        },
        error: (error) => {
          console.error('Error loading worker demographics:', error);
          this.loadingDemographics = false;
        }
      });
  }
}
